import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/base/Loading";
import SectionHeading from "@/components/base/SectionHeading";
import { formatDate, formatNumber, formatPercent } from "@/lib/format";
import type { PlatformStats, SystemMetric } from "@/pages/home/hooks/useHomeData";

interface StatusPanelProps {
  metrics: SystemMetric[];
  stats: PlatformStats | null;
  loading: boolean;
}

const STATUS_COLORS = [
  "oklch(var(--primary-500))",
  "oklch(var(--accent-500))",
  "oklch(var(--secondary-500))",
  "oklch(var(--foreground-400))",
];

const tooltipStyle = {
  borderRadius: "8px",
  border: "1px solid oklch(var(--background-300))",
  fontSize: "12px",
  backgroundColor: "oklch(var(--background-50))",
  color: "oklch(var(--foreground-950))",
};

export default function StatusPanel({ metrics, stats, loading }: StatusPanelProps) {
  const { t } = useTranslation();
  const activationKey = t("status.legendActivation");
  const issuedKey = t("status.legendIssued");

  const chartData = metrics.map((metric) => ({
    date: formatDate(metric.metric_date),
    [activationKey]: metric.activations,
    [issuedKey]: metric.licenses_issued,
  }));

  const latest = metrics.length > 0 ? metrics[metrics.length - 1] : null;
  const avgUptime =
    metrics.length > 0 ? metrics.reduce((sum, item) => sum + Number(item.uptime), 0) / metrics.length : null;
  const totalIssued = metrics.reduce((sum, item) => sum + item.licenses_issued, 0);

  const distribution = [
    { name: t("status.active"), value: stats?.active_licenses ?? 0 },
    { name: t("status.expired"), value: stats?.expired_licenses ?? 0 },
    { name: t("status.suspended"), value: stats?.suspended_licenses ?? 0 },
    { name: t("status.revoked"), value: stats?.revoked_licenses ?? 0 },
  ];
  const hasDistribution = distribution.some((item) => item.value > 0);

  const miniStats = [
    { label: t("status.statRequests"), value: latest ? formatNumber(latest.api_requests) : "--", icon: "ri-pulse-line" },
    { label: t("status.statUptime"), value: formatPercent(avgUptime), icon: "ri-shield-check-line" },
    { label: t("status.statIssued"), value: formatNumber(totalIssued), icon: "ri-file-add-line" },
  ];

  return (
    <section id="status" className="scroll-mt-20 bg-background-50">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 md:px-6 md:py-20">
        <SectionHeading
          eyebrow={t("status.eyebrow")}
          title={t("status.title")}
          description={t("status.description")}
        />

        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 lg:col-span-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-950">{t("status.trendTitle")}</h3>
                <p className="mt-1 text-xs text-foreground-500">{t("status.trendSub")}</p>
              </div>
              <div className="flex items-center gap-4 text-xs text-foreground-600">
                <span className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-primary-500" /> {activationKey}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-accent-500" /> {issuedKey}
                </span>
              </div>
            </div>

            <div className="mt-6 h-[280px] w-full">
              {loading ? (
                <Skeleton className="h-full w-full" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="fillActivations" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="oklch(var(--primary-500))" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="oklch(var(--primary-500))" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="fillIssued" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="oklch(var(--accent-500))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="oklch(var(--accent-500))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="4 4" stroke="oklch(var(--background-300))" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: "oklch(var(--foreground-500))" }}
                      tickLine={false}
                      axisLine={false}
                      minTickGap={24}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "oklch(var(--foreground-500))" }}
                      tickLine={false}
                      axisLine={false}
                      width={44}
                    />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "oklch(var(--foreground-600))" }} />
                    <Area
                      type="monotone"
                      dataKey={activationKey}
                      stroke="oklch(var(--primary-500))"
                      strokeWidth={2}
                      fill="url(#fillActivations)"
                    />
                    <Area
                      type="monotone"
                      dataKey={issuedKey}
                      stroke="oklch(var(--accent-500))"
                      strokeWidth={2}
                      fill="url(#fillIssued)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h3 className="font-heading text-base font-semibold text-foreground-950">{t("status.distTitle")}</h3>
            <p className="mt-1 text-xs text-foreground-500">{t("status.distSub")}</p>
            <div className="mt-4 h-[180px] w-full">
              {loading ? (
                <Skeleton className="h-full w-full" />
              ) : hasDistribution ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={distribution}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={52}
                      outerRadius={82}
                      paddingAngle={3}
                      stroke="none"
                    >
                      {distribution.map((entry, index) => (
                        <Cell key={entry.name} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-foreground-500">
                  {t("status.empty")}
                </div>
              )}
            </div>
            <ul className="mt-4 space-y-2">
              {distribution.map((item, index) => (
                <li key={item.name} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-foreground-700">
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: STATUS_COLORS[index % STATUS_COLORS.length] }}
                    />
                    {item.name}
                  </span>
                  <span className="font-medium text-foreground-950">{formatNumber(item.value)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          {miniStats.map((item) => (
            <div key={item.label} className="rounded-lg border border-background-200 bg-background-100 p-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 text-foreground-700">
                <i className={`${item.icon} text-lg`} />
              </span>
              <p className="mt-3 font-heading text-2xl font-semibold text-foreground-950">{item.value}</p>
              <p className="mt-1 text-xs text-foreground-500">{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}